import { Component, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type CommKind =
  | 'circular'
  | 'event'
  | 'announcement'
  | 'meeting'
  | 'counselling'
  | 'complaint'
  | 'reminder';

export type CommTab = 'overview' | CommKind;

export interface CommItem {
  id: number;
  kind: CommKind;
  ref_no: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  audience: string;
  date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  venue: string;
  issued_by: string;
  class_name: string;
  person_name: string;
  person_role: string;
  related_to: string;
  notes: string;
  follow_up: string;
  attachment: string;
  duration_mins: number | null;
  created_at: string;
  updated_at: string;
}

export interface ColDef {
  key: keyof CommItem | 'datetime';
  label: string;
  type: 'text' | 'badge' | 'date' | 'priority' | 'datetime' | 'person';
}

export interface FormFieldDef {
  key: keyof CommItem;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'date' | 'time' | 'number';
  col: string;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  showWhen?: { key: keyof CommItem; value: string };
}

export interface KindMeta {
  key: CommKind;
  label: string;
  plural: string;
  icon: string;
  color: string;
  gradient: string;
  addLabel: string;
  searchPlaceholder: string;
  statuses: string[];
  categories: string[];
  usePriority: boolean;
  useAudience: boolean;
  columns: ColDef[];
  fields: FormFieldDef[];
}

// ──────────────────────────────────────────────────────────
// KIND CONFIG
// ──────────────────────────────────────────────────────────
const AUDIENCES = ['All', 'Staff', 'Students', 'Parents', 'Management', 'Class-specific'];
const PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'];
const PERSON_ROLES = ['Student', 'Parent', 'Staff', 'Other'];
const CLASSES = [
  'Class VI', 'Class VII', 'Class VIII', 'Class IX', 'Class X',
  'Class XI – Science', 'Class XI – Commerce', 'Class XI – Arts',
  'Class XII – Science', 'Class XII – Commerce', 'BCA', 'BBA',
];
const STAFF = [
  'Admin User', 'Mrs. Priya Sharma', 'Mr. Rajesh Kumar', 'Dr. Anil Mehta',
  'Ms. Kavita Nair', 'Mr. Suresh Babu', 'Mrs. Meena Iyer', 'Counsellor Anita Desai',
  'Principal Dr. R. Krishnan', 'Vice Principal Mrs. S. Banerjee',
];

export const KIND_META: Record<CommKind, KindMeta> = {
  circular: {
    key: 'circular',
    label: 'Circular',
    plural: 'Circulars',
    icon: 'bi-file-earmark-text-fill',
    color: '#1a3a6b',
    gradient: 'linear-gradient(135deg,#1a3a6b,#2d6a9f)',
    addLabel: 'New Circular',
    searchPlaceholder: 'Search circulars by title, number, issuer…',
    statuses: ['Draft', 'Published', 'Archived'],
    categories: ['Academic', 'Administrative', 'Examination', 'Holiday', 'Transport', 'Fee', 'General'],
    usePriority: false,
    useAudience: true,
    columns: [
      { key: 'ref_no', label: 'Number', type: 'text' },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'category', label: 'Category', type: 'badge' },
      { key: 'audience', label: 'Audience', type: 'text' },
      { key: 'date', label: 'Issue Date', type: 'date' },
      { key: 'issued_by', label: 'Issued By', type: 'text' },
      { key: 'status', label: 'Status', type: 'badge' },
    ],
    fields: [
      { key: 'title', label: 'Title', type: 'text', col: 'col-md-8', required: true, placeholder: 'e.g. Mid-term examination schedule' },
      { key: 'category', label: 'Category', type: 'select', col: 'col-md-4', required: true, options: ['Academic', 'Administrative', 'Examination', 'Holiday', 'Transport', 'Fee', 'General'] },
      { key: 'date', label: 'Issue Date', type: 'date', col: 'col-md-4', required: true },
      { key: 'issued_by', label: 'Issued By', type: 'select', col: 'col-md-4', required: true, options: STAFF },
      { key: 'status', label: 'Status', type: 'select', col: 'col-md-4', required: true, options: ['Draft', 'Published', 'Archived'] },
      { key: 'audience', label: 'Audience', type: 'select', col: 'col-md-4', required: true, options: AUDIENCES },
      { key: 'class_name', label: 'Class', type: 'select', col: 'col-md-4', options: CLASSES, showWhen: { key: 'audience', value: 'Class-specific' } },
      { key: 'attachment', label: 'Attachment Name', type: 'text', col: 'col-md-4', placeholder: 'e.g. exam-schedule.pdf' },
      { key: 'description', label: 'Circular Body', type: 'textarea', col: 'col-12', required: true, placeholder: 'Full circular text…' },
    ],
  },
  event: {
    key: 'event',
    label: 'Event',
    plural: 'Events',
    icon: 'bi-calendar-event-fill',
    color: '#059669',
    gradient: 'linear-gradient(135deg,#059669,#10b981)',
    addLabel: 'New Event',
    searchPlaceholder: 'Search events by title, venue, organizer…',
    statuses: ['Upcoming', 'Ongoing', 'Completed', 'Cancelled'],
    categories: ['Academic', 'Sports', 'Cultural', 'Holiday', 'Workshop', 'Competition', 'Other'],
    usePriority: false,
    useAudience: true,
    columns: [
      { key: 'title', label: 'Event', type: 'text' },
      { key: 'category', label: 'Type', type: 'badge' },
      { key: 'datetime', label: 'When', type: 'datetime' },
      { key: 'venue', label: 'Venue', type: 'text' },
      { key: 'issued_by', label: 'Organizer', type: 'text' },
      { key: 'audience', label: 'Audience', type: 'text' },
      { key: 'status', label: 'Status', type: 'badge' },
    ],
    fields: [
      { key: 'title', label: 'Event Title', type: 'text', col: 'col-md-8', required: true, placeholder: 'e.g. Annual Day Celebration' },
      { key: 'category', label: 'Event Type', type: 'select', col: 'col-md-4', required: true, options: ['Academic', 'Sports', 'Cultural', 'Holiday', 'Workshop', 'Competition', 'Other'] },
      { key: 'date', label: 'Start Date', type: 'date', col: 'col-md-3', required: true },
      { key: 'end_date', label: 'End Date', type: 'date', col: 'col-md-3' },
      { key: 'start_time', label: 'Start Time', type: 'time', col: 'col-md-3' },
      { key: 'end_time', label: 'End Time', type: 'time', col: 'col-md-3' },
      { key: 'venue', label: 'Venue', type: 'text', col: 'col-md-4', required: true, placeholder: 'Auditorium / Ground / Hall' },
      { key: 'issued_by', label: 'Organizer', type: 'select', col: 'col-md-4', required: true, options: STAFF },
      { key: 'status', label: 'Status', type: 'select', col: 'col-md-4', required: true, options: ['Upcoming', 'Ongoing', 'Completed', 'Cancelled'] },
      { key: 'audience', label: 'Audience', type: 'select', col: 'col-md-4', options: AUDIENCES },
      { key: 'class_name', label: 'Class', type: 'select', col: 'col-md-4', options: CLASSES, showWhen: { key: 'audience', value: 'Class-specific' } },
      { key: 'description', label: 'Description', type: 'textarea', col: 'col-12', placeholder: 'Event details, dress code, notes…' },
    ],
  },
  announcement: {
    key: 'announcement',
    label: 'Announcement',
    plural: 'Announcements',
    icon: 'bi-megaphone-fill',
    color: '#0891b2',
    gradient: 'linear-gradient(135deg,#0891b2,#06b6d4)',
    addLabel: 'New Announcement',
    searchPlaceholder: 'Search announcements by title or body…',
    statuses: ['Draft', 'Active', 'Expired'],
    categories: ['General', 'Academic', 'Emergency', 'Holiday', 'Fee', 'Transport'],
    usePriority: true,
    useAudience: true,
    columns: [
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'category', label: 'Category', type: 'badge' },
      { key: 'priority', label: 'Priority', type: 'priority' },
      { key: 'audience', label: 'Audience', type: 'text' },
      { key: 'date', label: 'Published', type: 'date' },
      { key: 'end_date', label: 'Expires', type: 'date' },
      { key: 'status', label: 'Status', type: 'badge' },
    ],
    fields: [
      { key: 'title', label: 'Title', type: 'text', col: 'col-md-8', required: true, placeholder: 'Short announcement headline' },
      { key: 'category', label: 'Category', type: 'select', col: 'col-md-4', required: true, options: ['General', 'Academic', 'Emergency', 'Holiday', 'Fee', 'Transport'] },
      { key: 'priority', label: 'Priority', type: 'select', col: 'col-md-4', required: true, options: PRIORITIES },
      { key: 'audience', label: 'Audience', type: 'select', col: 'col-md-4', required: true, options: AUDIENCES },
      { key: 'status', label: 'Status', type: 'select', col: 'col-md-4', required: true, options: ['Draft', 'Active', 'Expired'] },
      { key: 'date', label: 'Publish Date', type: 'date', col: 'col-md-4', required: true },
      { key: 'end_date', label: 'Expiry Date', type: 'date', col: 'col-md-4' },
      { key: 'issued_by', label: 'Posted By', type: 'select', col: 'col-md-4', required: true, options: STAFF },
      { key: 'class_name', label: 'Class', type: 'select', col: 'col-md-4', options: CLASSES, showWhen: { key: 'audience', value: 'Class-specific' } },
      { key: 'description', label: 'Announcement Body', type: 'textarea', col: 'col-12', required: true, placeholder: 'Full announcement text…' },
    ],
  },
  meeting: {
    key: 'meeting',
    label: 'Meeting',
    plural: 'Meetings',
    icon: 'bi-people-fill',
    color: '#7c3aed',
    gradient: 'linear-gradient(135deg,#7c3aed,#8b5cf6)',
    addLabel: 'New Meeting',
    searchPlaceholder: 'Search meetings by title, chair, venue…',
    statuses: ['Scheduled', 'Completed', 'Cancelled'],
    categories: ['Staff', 'PTM', 'Department', 'Committee', 'Board', 'Emergency'],
    usePriority: false,
    useAudience: false,
    columns: [
      { key: 'title', label: 'Meeting', type: 'text' },
      { key: 'category', label: 'Type', type: 'badge' },
      { key: 'datetime', label: 'When', type: 'datetime' },
      { key: 'venue', label: 'Venue', type: 'text' },
      { key: 'issued_by', label: 'Chairperson', type: 'text' },
      { key: 'person_name', label: 'Attendees', type: 'text' },
      { key: 'status', label: 'Status', type: 'badge' },
    ],
    fields: [
      { key: 'title', label: 'Meeting Title', type: 'text', col: 'col-md-8', required: true, placeholder: 'e.g. Monthly staff review' },
      { key: 'category', label: 'Meeting Type', type: 'select', col: 'col-md-4', required: true, options: ['Staff', 'PTM', 'Department', 'Committee', 'Board', 'Emergency'] },
      { key: 'date', label: 'Date', type: 'date', col: 'col-md-4', required: true },
      { key: 'start_time', label: 'Start Time', type: 'time', col: 'col-md-4', required: true },
      { key: 'end_time', label: 'End Time', type: 'time', col: 'col-md-4' },
      { key: 'venue', label: 'Venue', type: 'text', col: 'col-md-4', required: true, placeholder: 'Conference Room / Online' },
      { key: 'issued_by', label: 'Chairperson', type: 'select', col: 'col-md-4', required: true, options: STAFF },
      { key: 'status', label: 'Status', type: 'select', col: 'col-md-4', required: true, options: ['Scheduled', 'Completed', 'Cancelled'] },
      { key: 'person_name', label: 'Attendees', type: 'text', col: 'col-12', placeholder: 'Names or groups invited' },
      { key: 'description', label: 'Agenda', type: 'textarea', col: 'col-12', required: true, placeholder: 'Meeting agenda…' },
      { key: 'notes', label: 'Minutes', type: 'textarea', col: 'col-12', placeholder: 'Fill after the meeting…' },
    ],
  },
  counselling: {
    key: 'counselling',
    label: 'Counselling',
    plural: 'Counselling',
    icon: 'bi-heart-pulse-fill',
    color: '#be185d',
    gradient: 'linear-gradient(135deg,#be185d,#ec4899)',
    addLabel: 'New Session',
    searchPlaceholder: 'Search by student, counsellor, type…',
    statuses: ['Scheduled', 'Completed', 'Cancelled', 'Follow-up'],
    categories: ['Academic', 'Career', 'Behavioral', 'Emotional', 'Parent Conference'],
    usePriority: false,
    useAudience: false,
    columns: [
      { key: 'person_name', label: 'Student', type: 'person' },
      { key: 'class_name', label: 'Class', type: 'text' },
      { key: 'category', label: 'Session Type', type: 'badge' },
      { key: 'issued_by', label: 'Counsellor', type: 'text' },
      { key: 'datetime', label: 'When', type: 'datetime' },
      { key: 'follow_up', label: 'Follow-up', type: 'date' },
      { key: 'status', label: 'Status', type: 'badge' },
    ],
    fields: [
      { key: 'person_name', label: 'Student Name', type: 'text', col: 'col-md-5', required: true, placeholder: 'Student full name' },
      { key: 'class_name', label: 'Class', type: 'select', col: 'col-md-3', required: true, options: CLASSES },
      { key: 'category', label: 'Session Type', type: 'select', col: 'col-md-4', required: true, options: ['Academic', 'Career', 'Behavioral', 'Emotional', 'Parent Conference'] },
      { key: 'date', label: 'Date', type: 'date', col: 'col-md-3', required: true },
      { key: 'start_time', label: 'Time', type: 'time', col: 'col-md-3', required: true },
      { key: 'duration_mins', label: 'Duration (mins)', type: 'number', col: 'col-md-3' },
      { key: 'status', label: 'Status', type: 'select', col: 'col-md-3', required: true, options: ['Scheduled', 'Completed', 'Cancelled', 'Follow-up'] },
      { key: 'issued_by', label: 'Counsellor', type: 'select', col: 'col-md-6', required: true, options: STAFF },
      { key: 'follow_up', label: 'Follow-up Date', type: 'date', col: 'col-md-6' },
      { key: 'title', label: 'Topic', type: 'text', col: 'col-12', required: true, placeholder: 'Session topic' },
      { key: 'description', label: 'Session Notes', type: 'textarea', col: 'col-12', placeholder: 'Confidential notes…' },
    ],
  },
  complaint: {
    key: 'complaint',
    label: 'Complaint',
    plural: 'Complaints',
    icon: 'bi-exclamation-octagon-fill',
    color: '#dc2626',
    gradient: 'linear-gradient(135deg,#dc2626,#ef4444)',
    addLabel: 'New Complaint',
    searchPlaceholder: 'Search tickets, complainant, subject…',
    statuses: ['Open', 'In Progress', 'Resolved', 'Closed', 'Rejected'],
    categories: ['Academic', 'Infrastructure', 'Transport', 'Fee', 'Staff', 'Bullying', 'Other'],
    usePriority: true,
    useAudience: false,
    columns: [
      { key: 'ref_no', label: 'Ticket', type: 'text' },
      { key: 'title', label: 'Subject', type: 'text' },
      { key: 'person_name', label: 'Complainant', type: 'person' },
      { key: 'category', label: 'Category', type: 'badge' },
      { key: 'priority', label: 'Priority', type: 'priority' },
      { key: 'issued_by', label: 'Assigned To', type: 'text' },
      { key: 'status', label: 'Status', type: 'badge' },
    ],
    fields: [
      { key: 'title', label: 'Subject', type: 'text', col: 'col-md-8', required: true, placeholder: 'Brief subject of the complaint' },
      { key: 'category', label: 'Category', type: 'select', col: 'col-md-4', required: true, options: ['Academic', 'Infrastructure', 'Transport', 'Fee', 'Staff', 'Bullying', 'Other'] },
      { key: 'person_name', label: 'Complainant', type: 'text', col: 'col-md-4', required: true, placeholder: 'Name' },
      { key: 'person_role', label: 'Complainant Type', type: 'select', col: 'col-md-4', required: true, options: PERSON_ROLES },
      { key: 'priority', label: 'Priority', type: 'select', col: 'col-md-4', required: true, options: PRIORITIES },
      { key: 'related_to', label: 'Against / Related To', type: 'text', col: 'col-md-4', placeholder: 'Person, dept, or facility' },
      { key: 'issued_by', label: 'Assigned To', type: 'select', col: 'col-md-4', options: STAFF },
      { key: 'status', label: 'Status', type: 'select', col: 'col-md-4', required: true, options: ['Open', 'In Progress', 'Resolved', 'Closed', 'Rejected'] },
      { key: 'date', label: 'Filed On', type: 'date', col: 'col-md-4', required: true },
      { key: 'attachment', label: 'Evidence / File', type: 'text', col: 'col-md-4', placeholder: 'Optional file name' },
      { key: 'description', label: 'Details', type: 'textarea', col: 'col-12', required: true, placeholder: 'Describe the issue…' },
      { key: 'notes', label: 'Resolution Notes', type: 'textarea', col: 'col-12', placeholder: 'Action taken / resolution…' },
    ],
  },
  reminder: {
    key: 'reminder',
    label: 'Reminder',
    plural: 'Reminders',
    icon: 'bi-alarm-fill',
    color: '#d97706',
    gradient: 'linear-gradient(135deg,#d97706,#f59e0b)',
    addLabel: 'New Reminder',
    searchPlaceholder: 'Search reminders by title or assignee…',
    statuses: ['Pending', 'Done', 'Overdue'],
    categories: ['None', 'Daily', 'Weekly', 'Monthly'],
    usePriority: true,
    useAudience: false,
    columns: [
      { key: 'title', label: 'Reminder', type: 'text' },
      { key: 'datetime', label: 'Due', type: 'datetime' },
      { key: 'issued_by', label: 'Assigned To', type: 'text' },
      { key: 'priority', label: 'Priority', type: 'priority' },
      { key: 'related_to', label: 'Related To', type: 'text' },
      { key: 'category', label: 'Repeat', type: 'text' },
      { key: 'status', label: 'Status', type: 'badge' },
    ],
    fields: [
      { key: 'title', label: 'Title', type: 'text', col: 'col-md-8', required: true, placeholder: 'What should be remembered?' },
      { key: 'priority', label: 'Priority', type: 'select', col: 'col-md-4', required: true, options: PRIORITIES },
      { key: 'date', label: 'Due Date', type: 'date', col: 'col-md-4', required: true },
      { key: 'start_time', label: 'Due Time', type: 'time', col: 'col-md-4' },
      { key: 'status', label: 'Status', type: 'select', col: 'col-md-4', required: true, options: ['Pending', 'Done', 'Overdue'] },
      { key: 'issued_by', label: 'Assigned To', type: 'select', col: 'col-md-4', required: true, options: STAFF },
      { key: 'category', label: 'Repeat', type: 'select', col: 'col-md-4', options: ['None', 'Daily', 'Weekly', 'Monthly'] },
      { key: 'related_to', label: 'Related To', type: 'text', col: 'col-md-4', placeholder: 'Module, person, or task' },
      { key: 'description', label: 'Notes', type: 'textarea', col: 'col-12', placeholder: 'Extra details…' },
    ],
  },
};

export const COMM_TABS: { key: CommTab; label: string; icon: string }[] = [
  { key: 'overview', label: 'Overview', icon: 'bi-grid-fill' },
  { key: 'circular', label: 'Circulars', icon: 'bi-file-earmark-text-fill' },
  { key: 'event', label: 'Events', icon: 'bi-calendar-event-fill' },
  { key: 'announcement', label: 'Announcements', icon: 'bi-megaphone-fill' },
  { key: 'meeting', label: 'Meetings', icon: 'bi-people-fill' },
  { key: 'counselling', label: 'Counselling', icon: 'bi-heart-pulse-fill' },
  { key: 'complaint', label: 'Complaints', icon: 'bi-exclamation-octagon-fill' },
  { key: 'reminder', label: 'Reminders', icon: 'bi-alarm-fill' },
];

const PREFIX: Record<CommKind, string> = {
  circular: 'CIR',
  event: 'EVT',
  announcement: 'ANN',
  meeting: 'MTG',
  counselling: 'CSN',
  complaint: 'CMP',
  reminder: 'REM',
};

// ──────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────
@Component({
  selector: 'app-communications',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './communications.html',
  styleUrl: './communications.scss',
})
export class Communications {
  readonly kinds = KIND_META;
  readonly tabs = COMM_TABS;
  readonly today = '2026-09-15';

  activeTab = signal<CommTab>('overview');
  toast = signal('');

  showForm = signal(false);
  showDetail = signal(false);
  showDelete = signal(false);
  isEditing = signal(false);
  viewing = signal<CommItem | null>(null);
  deleteTarget = signal<CommItem | null>(null);

  form: Partial<CommItem> = {};
  formErrors: Record<string, string> = {};
  formSubmitted = false;
  /** Bumps so computed form fields re-evaluate when ngModel writes to `form`. */
  formRev = signal(0);

  search = signal('');
  statusFilter = signal('');
  categoryFilter = signal('');
  priorityFilter = signal('');
  dateFrom = signal('');
  dateTo = signal('');
  sortKey = signal<string>('date');
  sortAsc = signal(false);
  currentPage = signal(1);
  pageSize = 8;

  private nextId = 51;
  private seq: Record<CommKind, number> = {
    circular: 17,
    event: 9,
    announcement: 7,
    meeting: 7,
    counselling: 7,
    complaint: 9,
    reminder: 9,
  };

  items = signal<CommItem[]>(this.buildSeed());

  // ── Derived ──────────────────────────────────────────────
  readonly listKind = computed<CommKind | null>(() => {
    const t = this.activeTab();
    return t === 'overview' ? null : t;
  });

  readonly meta = computed(() => {
    const k = this.listKind();
    return k ? KIND_META[k] : null;
  });

  readonly stats = computed(() => {
    const all = this.withEffectiveStatus(this.items());
    const byKind = (k: CommKind) => all.filter(i => i.kind === k);
    return {
      circulars: byKind('circular').length,
      publishedCirculars: byKind('circular').filter(i => i.status === 'Published').length,
      events: byKind('event').length,
      upcomingEvents: byKind('event').filter(i => i.status === 'Upcoming').length,
      announcements: byKind('announcement').filter(i => i.status === 'Active').length,
      meetings: byKind('meeting').filter(i => i.status === 'Scheduled').length,
      counselling: byKind('counselling').filter(i => i.status === 'Scheduled' || i.status === 'Follow-up').length,
      openComplaints: byKind('complaint').filter(i => i.status === 'Open' || i.status === 'In Progress').length,
      overdueReminders: byKind('reminder').filter(i => i.status === 'Overdue').length,
      pendingReminders: byKind('reminder').filter(i => i.status === 'Pending').length,
      total: all.length,
    };
  });

  readonly upcomingFeed = computed(() => {
    const all = this.withEffectiveStatus(this.items());
    return all
      .filter(i =>
        (i.kind === 'event' && (i.status === 'Upcoming' || i.status === 'Ongoing')) ||
        (i.kind === 'meeting' && i.status === 'Scheduled') ||
        (i.kind === 'counselling' && (i.status === 'Scheduled' || i.status === 'Follow-up'))
      )
      .sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time))
      .slice(0, 6);
  });

  readonly attentionItems = computed(() => {
    const all = this.withEffectiveStatus(this.items());
    return all
      .filter(i =>
        (i.kind === 'complaint' && (i.status === 'Open' || i.status === 'In Progress')) ||
        (i.kind === 'reminder' && (i.status === 'Overdue' || (i.status === 'Pending' && i.priority === 'Urgent'))) ||
        (i.kind === 'announcement' && i.priority === 'Urgent' && i.status === 'Active')
      )
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 6);
  });

  readonly recentCirculars = computed(() =>
    this.items()
      .filter(i => i.kind === 'circular')
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 5)
  );

  readonly filtered = computed(() => {
    const kind = this.listKind();
    if (!kind) return [];
    const q = this.search().toLowerCase().trim();
    const st = this.statusFilter();
    const cat = this.categoryFilter();
    const pri = this.priorityFilter();
    const from = this.dateFrom();
    const to = this.dateTo();
    const key = this.sortKey();
    const asc = this.sortAsc();

    const rows = this.withEffectiveStatus(this.items())
      .filter(i => i.kind === kind)
      .filter(i => {
        const matchQ = !q || [
          i.title, i.ref_no, i.description, i.issued_by, i.person_name,
          i.venue, i.category, i.audience, i.related_to, i.class_name, i.notes,
        ].some(v => (v ?? '').toLowerCase().includes(q));
        const matchS = !st || this.effectiveStatus(i) === st;
        const matchC = !cat || i.category === cat;
        const matchP = !pri || i.priority === pri;
        const matchFrom = !from || i.date >= from;
        const matchTo = !to || i.date <= to;
        return matchQ && matchS && matchC && matchP && matchFrom && matchTo;
      });

    return rows.sort((a, b) => {
      const av = String(this.sortValue(a, key) ?? '').toLowerCase();
      const bv = String(this.sortValue(b, key) ?? '').toLowerCase();
      if (av < bv) return asc ? -1 : 1;
      if (av > bv) return asc ? 1 : -1;
      return 0;
    });
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filtered().length / this.pageSize))
  );

  readonly paged = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize;
    return this.filtered().slice(start, start + this.pageSize);
  });

  readonly visibleFormFields = computed(() => {
    this.formRev();
    const kind = (this.form.kind ?? this.listKind()) as CommKind | null;
    if (!kind) return [];
    return KIND_META[kind].fields.filter(f => {
      if (!f.showWhen) return true;
      return String(this.form[f.showWhen.key] ?? '') === f.showWhen.value;
    });
  });

  get pageNumbers(): number[] {
    const t = this.totalPages();
    const c = Math.min(this.currentPage(), t);
    const out: number[] = [];
    for (let i = Math.max(1, c - 2); i <= Math.min(t, c + 2); i++) out.push(i);
    return out;
  }

  get hasActiveFilters(): boolean {
    return !!(this.search() || this.statusFilter() || this.categoryFilter() ||
      this.priorityFilter() || this.dateFrom() || this.dateTo());
  }

  // ── Tab / filter ─────────────────────────────────────────
  setTab(tab: CommTab): void {
    this.activeTab.set(tab);
    this.resetFilters();
    const kind = tab === 'overview' ? null : tab;
    this.sortKey.set(kind === 'complaint' || kind === 'circular' ? 'ref_no' : 'date');
    this.sortAsc.set(false);
  }

  resetFilters(): void {
    this.search.set('');
    this.statusFilter.set('');
    this.categoryFilter.set('');
    this.priorityFilter.set('');
    this.dateFrom.set('');
    this.dateTo.set('');
    this.currentPage.set(1);
  }

  onSearch(value: string): void {
    this.search.set(value);
    this.currentPage.set(1);
  }

  sort(key: string): void {
    if (this.sortKey() === key) this.sortAsc.update(v => !v);
    else { this.sortKey.set(key); this.sortAsc.set(true); }
    this.currentPage.set(1);
  }

  goToPage(p: number): void {
    if (p >= 1 && p <= this.totalPages()) this.currentPage.set(p);
  }

  // ── CRUD ─────────────────────────────────────────────────
  openAdd(kind?: CommKind): void {
    const k = kind ?? this.listKind();
    if (!k) return;
    const meta = KIND_META[k];
    this.form = {
      kind: k,
      title: '',
      description: '',
      status: meta.statuses[0],
      priority: meta.usePriority ? 'Normal' : '',
      category: meta.categories[0],
      audience: meta.useAudience ? 'All' : '',
      date: this.today,
      end_date: '',
      start_time: '',
      end_time: '',
      venue: '',
      issued_by: STAFF[0],
      class_name: '',
      person_name: '',
      person_role: k === 'complaint' ? 'Parent' : '',
      related_to: '',
      notes: '',
      follow_up: '',
      attachment: '',
      duration_mins: k === 'counselling' ? 30 : null,
    };
    this.formErrors = {};
    this.formSubmitted = false;
    this.formRev.update(n => n + 1);
    this.isEditing.set(false);
    this.showForm.set(true);
  }

  openEdit(item: CommItem): void {
    this.form = { ...item };
    this.formErrors = {};
    this.formSubmitted = false;
    this.formRev.update(n => n + 1);
    this.isEditing.set(true);
    this.showDetail.set(false);
    this.showForm.set(true);
  }

  openDetail(item: CommItem): void {
    this.viewing.set({ ...item, status: this.effectiveStatus(item) });
    this.showDetail.set(true);
  }

  closeForm(): void {
    this.showForm.set(false);
    this.formSubmitted = false;
    this.formErrors = {};
  }

  touchForm(): void {
    this.formRev.update(n => n + 1);
  }

  save(): void {
    this.formSubmitted = true;
    if (!this.validate()) return;

    const kind = this.form.kind as CommKind;
    const now = new Date().toISOString();

    if (this.isEditing() && this.form.id != null) {
      this.items.update(list =>
        list.map(r => r.id === this.form.id
          ? { ...r, ...this.blankItem(kind), ...this.form, id: r.id, ref_no: r.ref_no, kind, created_at: r.created_at, updated_at: now } as CommItem
          : r
        )
      );
      this.flash(`${KIND_META[kind].label} updated.`);
    } else {
      const created: CommItem = {
        ...this.blankItem(kind),
        ...this.form,
        id: this.nextId++,
        kind,
        ref_no: this.nextRef(kind),
        created_at: now,
        updated_at: now,
      } as CommItem;
      this.items.update(list => [created, ...list]);
      this.flash(`${KIND_META[kind].label} created.`);
      this.currentPage.set(1);
    }
    this.closeForm();
  }

  confirmDelete(item: CommItem): void {
    this.deleteTarget.set(item);
    this.showDelete.set(true);
    this.showDetail.set(false);
  }

  executeDelete(): void {
    const t = this.deleteTarget();
    if (!t) return;
    this.items.update(list => list.filter(r => r.id !== t.id));
    this.showDelete.set(false);
    this.deleteTarget.set(null);
    this.flash(`${KIND_META[t.kind].label} deleted.`);
    if (this.currentPage() > this.totalPages()) {
      this.currentPage.set(this.totalPages());
    }
  }

  cancelDelete(): void {
    this.showDelete.set(false);
    this.deleteTarget.set(null);
  }

  setStatus(item: CommItem, status: string): void {
    const now = new Date().toISOString();
    this.items.update(list =>
      list.map(r => r.id === item.id ? { ...r, status, updated_at: now } : r)
    );
    this.flash(`Marked as ${status}.`);
    this.showDetail.set(false);
  }

  // ── Display helpers ──────────────────────────────────────
  effectiveStatus(item: CommItem): string {
    if (item.kind === 'reminder' && item.status === 'Pending' && item.date && item.date < this.today) {
      return 'Overdue';
    }
    if (item.kind === 'announcement' && item.status === 'Active' && item.end_date && item.end_date < this.today) {
      return 'Expired';
    }
    return item.status;
  }

  formatDate(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso + (iso.includes('T') ? '' : 'T00:00:00'));
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  formatTime(t: string): string {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h)) return t;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = ((h + 11) % 12) + 1;
    return `${hr}:${String(m ?? 0).padStart(2, '0')} ${ampm}`;
  }

  displayCell(item: CommItem, col: ColDef): string {
    if (col.type === 'datetime') {
      const date = this.formatDate(item.date);
      const time = this.formatTime(item.start_time);
      return time ? `${date} · ${time}` : date;
    }
    if (col.type === 'date') {
      const key = col.key === 'datetime' ? 'date' : col.key;
      return this.formatDate(String(item[key] ?? ''));
    }
    if (col.type === 'person') {
      const role = item.person_role ? ` (${item.person_role})` : '';
      const cls = item.class_name && item.kind === 'counselling' ? '' : '';
      return (item.person_name || '—') + role + cls;
    }
    const raw = item[col.key as keyof CommItem];
    if (raw === null || raw === undefined || raw === '') return '—';
    return String(raw);
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      Draft: 'st-draft', Published: 'st-published', Archived: 'st-archived',
      Upcoming: 'st-upcoming', Ongoing: 'st-ongoing', Completed: 'st-completed', Cancelled: 'st-cancelled',
      Active: 'st-active', Expired: 'st-expired',
      Scheduled: 'st-scheduled', 'Follow-up': 'st-followup',
      Open: 'st-open', 'In Progress': 'st-progress', Resolved: 'st-resolved', Closed: 'st-closed', Rejected: 'st-rejected',
      Pending: 'st-pending', Done: 'st-done', Overdue: 'st-overdue',
    };
    return map[status] ?? 'st-draft';
  }

  priorityClass(priority: string): string {
    const map: Record<string, string> = {
      Low: 'pr-low', Normal: 'pr-normal', High: 'pr-high', Urgent: 'pr-urgent',
    };
    return map[priority] ?? 'pr-normal';
  }

  categoryClass(category: string): string {
    const palette = ['cat-a', 'cat-b', 'cat-c', 'cat-d', 'cat-e', 'cat-f', 'cat-g'];
    let hash = 0;
    for (let i = 0; i < category.length; i++) hash = (hash + category.charCodeAt(i)) % palette.length;
    return palette[hash];
  }

  sortIcon(key: string): string {
    if (this.sortKey() !== key) return 'bi-arrow-down-up opacity-25';
    return this.sortAsc() ? 'bi-sort-down-alt text-primary' : 'bi-sort-up text-primary';
  }

  kindBadge(kind: CommKind): KindMeta {
    return KIND_META[kind];
  }

  tabCount(tab: CommTab): number {
    const s = this.stats();
    if (tab === 'complaint') return s.openComplaints;
    if (tab === 'reminder') return s.overdueReminders;
    if (tab === 'counselling') return s.counselling;
    return 0;
  }

  quickStatuses(item: CommItem): string[] {
    return KIND_META[item.kind].statuses.filter(s => s !== this.effectiveStatus(item));
  }

  // ── Validation ───────────────────────────────────────────
  private validate(): boolean {
    this.formErrors = {};
    const kind = this.form.kind as CommKind | undefined;
    if (!kind) {
      this.formErrors['kind'] = 'Type is required.';
      return false;
    }
    for (const f of this.visibleFormFields()) {
      const val = this.form[f.key];
      const str = String(val ?? '').trim();
      if (f.required && (val === null || val === undefined || str === '')) {
        this.formErrors[f.key] = `${f.label} is required.`;
      }
    }
    return Object.keys(this.formErrors).length === 0;
  }

  private nextRef(kind: CommKind): string {
    const n = this.seq[kind]++;
    return `${PREFIX[kind]}-2026-${String(n).padStart(3, '0')}`;
  }

  private blankItem(kind: CommKind): CommItem {
    return {
      id: 0, kind, ref_no: '', title: '', description: '', status: '',
      priority: '', category: '', audience: '', date: '', end_date: '',
      start_time: '', end_time: '', venue: '', issued_by: '', class_name: '',
      person_name: '', person_role: '', related_to: '', notes: '',
      follow_up: '', attachment: '', duration_mins: null,
      created_at: '', updated_at: '',
    };
  }

  private withEffectiveStatus(list: CommItem[]): CommItem[] {
    return list.map(i => ({ ...i, status: this.effectiveStatus(i) }));
  }

  private sortValue(item: CommItem, key: string): unknown {
    if (key === 'datetime') return item.date + (item.start_time || '');
    if (key === 'status') return this.effectiveStatus(item);
    return item[key as keyof CommItem];
  }

  private flash(msg: string): void {
    this.toast.set(msg);
    setTimeout(() => this.toast.set(''), 2400);
  }

  // ── Seed data ────────────────────────────────────────────
  private buildSeed(): CommItem[] {
    const iso = (d: string) => `${d}T09:00:00.000Z`;
    const row = (partial: Partial<CommItem> & Pick<CommItem, 'id' | 'kind' | 'ref_no' | 'title' | 'status' | 'date'>): CommItem => ({
      ...this.blankItem(partial.kind),
      description: '',
      priority: 'Normal',
      category: '',
      audience: 'All',
      created_at: iso(partial.date),
      updated_at: iso(partial.date),
      ...partial,
    });

    return [
      // Circulars
      row({ id: 1, kind: 'circular', ref_no: 'CIR-2026-012', title: 'Mid-term examination schedule – Classes IX to XII', category: 'Examination', status: 'Published', audience: 'Students', date: '2026-09-08', issued_by: 'Principal Dr. R. Krishnan', attachment: 'midterm-schedule.pdf', description: 'Mid-term exams will be held from 20–28 September 2026. Seating plans will be displayed on notice boards.' }),
      row({ id: 2, kind: 'circular', ref_no: 'CIR-2026-013', title: 'Second quarter fee submission reminder', category: 'Fee', status: 'Published', audience: 'Parents', date: '2026-09-10', issued_by: 'Admin User', attachment: 'fee-q2.pdf', description: 'Last date for Q2 fee payment is 30 September 2026. Late fine of ₹50/day applies thereafter.' }),
      row({ id: 3, kind: 'circular', ref_no: 'CIR-2026-014', title: 'Updated school uniform policy (winter)', category: 'Administrative', status: 'Draft', audience: 'All', date: '2026-09-14', issued_by: 'Vice Principal Mrs. S. Banerjee', description: 'Winter uniform is compulsory from 1 November. Blazers available at the school store.' }),
      row({ id: 4, kind: 'circular', ref_no: 'CIR-2026-011', title: 'Teachers’ Day holiday and assembly', category: 'Holiday', status: 'Archived', audience: 'All', date: '2026-09-01', issued_by: 'Principal Dr. R. Krishnan', description: 'School remains closed on 5 September after the morning assembly.' }),
      row({ id: 5, kind: 'circular', ref_no: 'CIR-2026-015', title: 'Parent-Teacher Meeting – Term 1', category: 'Academic', status: 'Published', audience: 'Parents', date: '2026-09-12', issued_by: 'Mrs. Priya Sharma', description: 'PTM will be held on 27 September 2026, 9:00 AM to 1:00 PM. Time slots will be shared via SMS.' }),
      row({ id: 6, kind: 'circular', ref_no: 'CIR-2026-010', title: 'Bus route RT-02 diversion via Ring Road', category: 'Transport', status: 'Published', audience: 'Parents', date: '2026-08-28', issued_by: 'Mr. Suresh Babu', description: 'Due to metro construction, RT-02 will ply via Ring Road from 1 September.' }),
      row({ id: 7, kind: 'circular', ref_no: 'CIR-2026-016', title: 'Diwali vacation dates', category: 'Holiday', status: 'Draft', audience: 'All', date: '2026-09-15', issued_by: 'Principal Dr. R. Krishnan', description: 'Proposed vacation: 8–16 November 2026. Final dates subject to board confirmation.' }),
      row({ id: 8, kind: 'circular', ref_no: 'CIR-2026-009', title: 'Library week reading challenge', category: 'Academic', status: 'Archived', audience: 'Students', date: '2026-08-18', issued_by: 'Ms. Kavita Nair', class_name: '', description: 'Students to submit book reviews by 30 August.' }),

      // Events
      row({ id: 9,  kind: 'event', ref_no: 'EVT-2026-004', title: 'Science Exhibition 2026', category: 'Academic', status: 'Upcoming', audience: 'All', date: '2026-09-20', end_date: '2026-09-20', start_time: '10:00', end_time: '16:00', venue: 'Science Block', issued_by: 'Dr. Anil Mehta', description: 'Working models from Classes VIII–XII. Parents welcome after 1 PM.' }),
      row({ id: 10, kind: 'event', ref_no: 'EVT-2026-005', title: 'Inter-house sports meet', category: 'Sports', status: 'Upcoming', audience: 'Students', date: '2026-09-28', end_date: '2026-09-30', start_time: '08:00', end_time: '16:00', venue: 'Main Ground', issued_by: 'Mr. Rajesh Kumar', description: 'Athletics, football and basketball. March-past at 8:00 AM.' }),
      row({ id: 11, kind: 'event', ref_no: 'EVT-2026-003', title: 'Teachers’ Day cultural programme', category: 'Cultural', status: 'Completed', audience: 'All', date: '2026-09-05', start_time: '09:00', end_time: '12:00', venue: 'Auditorium', issued_by: 'Mrs. Meena Iyer', description: 'Student-led programme honouring teachers.' }),
      row({ id: 12, kind: 'event', ref_no: 'EVT-2026-006', title: 'NEP 2020 orientation workshop', category: 'Workshop', status: 'Upcoming', audience: 'Staff', date: '2026-09-22', start_time: '14:00', end_time: '17:00', venue: 'Conference Hall', issued_by: 'Principal Dr. R. Krishnan', description: 'Mandatory for all teaching staff.' }),
      row({ id: 13, kind: 'event', ref_no: 'EVT-2026-007', title: 'Gandhi Jayanti – Swachhata drive', category: 'Holiday', status: 'Upcoming', audience: 'All', date: '2026-10-02', start_time: '08:30', venue: 'Campus', issued_by: 'Vice Principal Mrs. S. Banerjee', description: 'Campus cleanliness drive followed by prayer meeting.' }),
      row({ id: 14, kind: 'event', ref_no: 'EVT-2026-008', title: 'Annual Day Celebration', category: 'Cultural', status: 'Upcoming', audience: 'All', date: '2026-10-15', start_time: '17:00', end_time: '21:00', venue: 'School Auditorium', issued_by: 'Mrs. Meena Iyer', description: 'Theme: Heritage of India. All staff and students to attend.' }),
      row({ id: 15, kind: 'event', ref_no: 'EVT-2026-002', title: 'Class picnic – Class IX', category: 'Other', status: 'Cancelled', audience: 'Class-specific', class_name: 'Class IX', date: '2026-09-18', venue: 'City Park', issued_by: 'Mrs. Priya Sharma', description: 'Cancelled due to weather advisory.' }),
      row({ id: 16, kind: 'event', ref_no: 'EVT-2026-001', title: 'Maths Olympiad (school round)', category: 'Competition', status: 'Completed', audience: 'Students', date: '2026-08-25', start_time: '09:00', venue: 'Exam Halls', issued_by: 'Dr. Anil Mehta', description: 'School-level screening completed.' }),

      // Announcements
      row({ id: 17, kind: 'announcement', ref_no: 'ANN-2026-004', title: 'Water tanker delay – carry water bottles', category: 'Emergency', status: 'Active', priority: 'Urgent', audience: 'All', date: '2026-09-15', end_date: '2026-09-16', issued_by: 'Admin User', description: 'Municipal supply disrupted today. Canteen will provide packaged water.' }),
      row({ id: 18, kind: 'announcement', ref_no: 'ANN-2026-005', title: 'Annual Day rehearsal schedule posted', category: 'General', status: 'Active', priority: 'Normal', audience: 'Students', date: '2026-09-13', end_date: '2026-10-15', issued_by: 'Mrs. Meena Iyer', description: 'Rehearsals daily 3:00–5:00 PM in the auditorium starting 16 September.' }),
      row({ id: 19, kind: 'announcement', ref_no: 'ANN-2026-003', title: 'Online fee portal downtime on Sunday', category: 'Fee', status: 'Active', priority: 'High', audience: 'Parents', date: '2026-09-12', end_date: '2026-09-21', issued_by: 'Admin User', description: 'Portal unavailable 21 September, 11 PM – 2 AM for maintenance.' }),
      row({ id: 20, kind: 'announcement', ref_no: 'ANN-2026-002', title: 'Independence Day photo gallery is live', category: 'General', status: 'Expired', priority: 'Low', audience: 'All', date: '2026-08-16', end_date: '2026-09-01', issued_by: 'Ms. Kavita Nair', description: 'Photos available on the parent portal.' }),
      row({ id: 21, kind: 'announcement', ref_no: 'ANN-2026-006', title: 'New library timings from October', category: 'Academic', status: 'Draft', priority: 'Normal', audience: 'Students', date: '2026-09-15', issued_by: 'Ms. Kavita Nair', description: 'Library will remain open until 5:30 PM on weekdays.' }),
      row({ id: 22, kind: 'announcement', ref_no: 'ANN-2026-001', title: 'Bus RT-04 delayed by 20 minutes', category: 'Transport', status: 'Expired', priority: 'High', audience: 'Parents', date: '2026-09-04', end_date: '2026-09-04', issued_by: 'Mr. Suresh Babu', description: 'Temporary delay due to traffic near the flyover.' }),

      // Meetings
      row({ id: 23, kind: 'meeting', ref_no: 'MTG-2026-004', title: 'Term-1 Parent-Teacher Meeting', category: 'PTM', status: 'Scheduled', date: '2026-09-27', start_time: '09:00', end_time: '13:00', venue: 'Respective Classrooms', issued_by: 'Principal Dr. R. Krishnan', person_name: 'All class teachers & parents', description: 'Discuss mid-term performance and attendance.' }),
      row({ id: 24, kind: 'meeting', ref_no: 'MTG-2026-005', title: 'Weekly staff briefing', category: 'Staff', status: 'Scheduled', date: '2026-09-16', start_time: '08:00', end_time: '08:40', venue: 'Conference Hall', issued_by: 'Principal Dr. R. Krishnan', person_name: 'All teaching staff', description: 'Week plan, exam invigilation, Annual Day duties.' }),
      row({ id: 25, kind: 'meeting', ref_no: 'MTG-2026-003', title: 'Science department review', category: 'Department', status: 'Completed', date: '2026-09-09', start_time: '15:00', end_time: '16:00', venue: 'Lab 2', issued_by: 'Dr. Anil Mehta', person_name: 'Science faculty', description: 'Lab inventory and exhibition prep.', notes: 'Exhibition stalls allotted. Budget approved for 4 models.' }),
      row({ id: 26, kind: 'meeting', ref_no: 'MTG-2026-006', title: 'Disciplinary committee – case review', category: 'Committee', status: 'Scheduled', date: '2026-09-18', start_time: '14:30', end_time: '15:30', venue: 'VP Office', issued_by: 'Vice Principal Mrs. S. Banerjee', person_name: 'Committee members', description: 'Review two pending student cases.' }),
      row({ id: 27, kind: 'meeting', ref_no: 'MTG-2026-002', title: 'Managing committee quarterly', category: 'Board', status: 'Completed', date: '2026-08-20', start_time: '11:00', venue: 'Board Room', issued_by: 'Principal Dr. R. Krishnan', person_name: 'Managing committee', description: 'Q1 academics and finance review.', notes: 'Fee revision deferred. Sports ground resurfacing approved.' }),
      row({ id: 28, kind: 'meeting', ref_no: 'MTG-2026-001', title: 'Emergency monsoon preparedness', category: 'Emergency', status: 'Cancelled', date: '2026-08-12', start_time: '16:00', venue: 'Conference Hall', issued_by: 'Admin User', person_name: 'Admin & transport', description: 'Cancelled after weather all-clear.' }),

      // Counselling
      row({ id: 29, kind: 'counselling', ref_no: 'CSN-2026-004', title: 'Career stream selection – Science vs Commerce', category: 'Career', status: 'Scheduled', date: '2026-09-17', start_time: '11:00', duration_mins: 40, issued_by: 'Counsellor Anita Desai', person_name: 'Arjun Sharma', class_name: 'Class XI – Science', follow_up: '2026-10-08', description: 'Discuss aptitude test results and subject load.' }),
      row({ id: 30, kind: 'counselling', ref_no: 'CSN-2026-005', title: 'Attendance and study habits', category: 'Academic', status: 'Follow-up', date: '2026-09-16', start_time: '12:30', duration_mins: 30, issued_by: 'Counsellor Anita Desai', person_name: 'Rahul Kumar', class_name: 'Class IX', follow_up: '2026-09-30', description: 'Second session after parent meeting. Monitor homework completion.' }),
      row({ id: 31, kind: 'counselling', ref_no: 'CSN-2026-003', title: 'Peer conflict mediation', category: 'Behavioral', status: 'Completed', date: '2026-09-08', start_time: '10:00', duration_mins: 45, issued_by: 'Counsellor Anita Desai', person_name: 'Vikram Singh', class_name: 'Class IX', description: 'Mediation with classmate completed. Both students agreed on classroom conduct.' }),
      row({ id: 32, kind: 'counselling', ref_no: 'CSN-2026-006', title: 'Exam anxiety support', category: 'Emotional', status: 'Scheduled', date: '2026-09-19', start_time: '13:00', duration_mins: 30, issued_by: 'Counsellor Anita Desai', person_name: 'Priya Mehta', class_name: 'Class XI – Science', description: 'Breathing techniques and revision planner.' }),
      row({ id: 33, kind: 'counselling', ref_no: 'CSN-2026-002', title: 'Parent conference – learning support', category: 'Parent Conference', status: 'Completed', date: '2026-09-03', start_time: '09:30', duration_mins: 40, issued_by: 'Mrs. Priya Sharma', person_name: 'Anita Singh', class_name: 'Class IX', description: 'Parents briefed on remedial classes twice a week.' }),
      row({ id: 34, kind: 'counselling', ref_no: 'CSN-2026-001', title: 'Career guidance – BCA options', category: 'Career', status: 'Cancelled', date: '2026-08-29', start_time: '15:00', issued_by: 'Counsellor Anita Desai', person_name: 'Sneha Patel', class_name: 'BCA', description: 'Student rescheduled due to practical exam.' }),

      // Complaints
      row({ id: 35, kind: 'complaint', ref_no: 'CMP-2026-006', title: 'Classroom projector not working – XI-A', category: 'Infrastructure', status: 'Open', priority: 'High', date: '2026-09-14', person_name: 'Mrs. Priya Sharma', person_role: 'Staff', related_to: 'IT / AV room', issued_by: 'Admin User', description: 'Projector in XI-A failed during the 3rd period. HDMI port unresponsive.' }),
      row({ id: 36, kind: 'complaint', ref_no: 'CMP-2026-007', title: 'Bus RT-02 late by 25 minutes for a week', category: 'Transport', status: 'In Progress', priority: 'Urgent', date: '2026-09-12', person_name: 'Mr. Mehta', person_role: 'Parent', related_to: 'Bus RT-02', issued_by: 'Mr. Suresh Babu', description: 'Pickup at Sector 21 has been delayed every morning this week.' }),
      row({ id: 37, kind: 'complaint', ref_no: 'CMP-2026-005', title: 'Canteen overcharging for sandwiches', category: 'Other', status: 'Resolved', priority: 'Normal', date: '2026-09-09', person_name: 'Mohit Gupta', person_role: 'Student', related_to: 'Canteen contractor', issued_by: 'Admin User', notes: 'Price list reprinted and displayed. Refund issued.', description: 'Sandwich billed at ₹60 against displayed ₹45.' }),
      row({ id: 38, kind: 'complaint', ref_no: 'CMP-2026-008', title: 'Fee receipt not generated after payment', category: 'Fee', status: 'Open', priority: 'High', date: '2026-09-15', person_name: 'Mrs. Nair', person_role: 'Parent', related_to: 'Fee portal', issued_by: 'Admin User', description: 'Payment of ₹18,400 succeeded on 14 Sep but receipt is missing on the portal.' }),
      row({ id: 39, kind: 'complaint', ref_no: 'CMP-2026-004', title: 'Name-calling in Class IX-B', category: 'Bullying', status: 'In Progress', priority: 'Urgent', date: '2026-09-10', person_name: 'Kavita Yadav', person_role: 'Student', related_to: 'Class IX-B peers', issued_by: 'Counsellor Anita Desai', description: 'Repeated name-calling during recess. Parent requested intervention.' }),
      row({ id: 40, kind: 'complaint', ref_no: 'CMP-2026-003', title: 'Lab stool broken – Chemistry Lab', category: 'Infrastructure', status: 'Closed', priority: 'Low', date: '2026-08-30', person_name: 'Dr. Anil Mehta', person_role: 'Staff', related_to: 'Maintenance', issued_by: 'Admin User', notes: 'Stool replaced on 2 Sep.', description: 'One wooden stool has a cracked leg.' }),
      row({ id: 41, kind: 'complaint', ref_no: 'CMP-2026-002', title: 'Incorrect marks entry in unit test', category: 'Academic', status: 'Rejected', priority: 'Normal', date: '2026-08-22', person_name: 'Rahul Kumar', person_role: 'Student', related_to: 'Mathematics', issued_by: 'Mrs. Priya Sharma', notes: 'Answer script rechecked. Original marks stand.', description: 'Student claims 4 marks not awarded in Q.5.' }),
      row({ id: 42, kind: 'complaint', ref_no: 'CMP-2026-001', title: 'Staff parking blocked by vendor van', category: 'Staff', status: 'Closed', priority: 'Low', date: '2026-08-19', person_name: 'Mr. Rajesh Kumar', person_role: 'Staff', related_to: 'Security', issued_by: 'Admin User', notes: 'Vendor instructed to use loading bay.', description: 'Canteen supply van parked in staff slots at 8 AM.' }),

      // Reminders
      row({ id: 43, kind: 'reminder', ref_no: 'REM-2026-006', title: 'Submit invigilation duty chart', category: 'None', status: 'Pending', priority: 'High', date: '2026-09-16', start_time: '12:00', issued_by: 'Vice Principal Mrs. S. Banerjee', related_to: 'Examinations', description: 'Share the signed chart with all HODs.' }),
      row({ id: 44, kind: 'reminder', ref_no: 'REM-2026-007', title: 'Renew bus RT-02 insurance', category: 'None', status: 'Pending', priority: 'Urgent', date: '2026-09-30', issued_by: 'Mr. Suresh Babu', related_to: 'Transport', description: 'Policy expires 30 Sep 2026.' }),
      row({ id: 45, kind: 'reminder', ref_no: 'REM-2026-005', title: 'Call parents of students below 75% attendance', category: 'Weekly', status: 'Overdue', priority: 'High', date: '2026-09-12', issued_by: 'Mrs. Priya Sharma', related_to: 'Attendance', description: '12 students flagged in the attendance module.' }),
      row({ id: 46, kind: 'reminder', ref_no: 'REM-2026-008', title: 'Upload Annual Day rehearsal photos', category: 'None', status: 'Pending', priority: 'Normal', date: '2026-09-18', start_time: '17:00', issued_by: 'Ms. Kavita Nair', related_to: 'Announcements' }),
      row({ id: 47, kind: 'reminder', ref_no: 'REM-2026-004', title: 'Prepare PTM progress remarks', category: 'None', status: 'Pending', priority: 'High', date: '2026-09-25', issued_by: 'Mrs. Priya Sharma', related_to: 'PTM', description: 'Remarks required for all students in XI-A.' }),
      row({ id: 48, kind: 'reminder', ref_no: 'REM-2026-003', title: 'Pay electricity bill – September', category: 'Monthly', status: 'Done', priority: 'Normal', date: '2026-09-10', issued_by: 'Admin User', related_to: 'Finance' }),
      row({ id: 49, kind: 'reminder', ref_no: 'REM-2026-002', title: 'Follow up counselling – Rahul Kumar', category: 'None', status: 'Pending', priority: 'Normal', date: '2026-09-16', start_time: '12:30', issued_by: 'Counsellor Anita Desai', related_to: 'Counselling' }),
      row({ id: 50, kind: 'reminder', ref_no: 'REM-2026-001', title: 'Order winter blazers for store', category: 'None', status: 'Done', priority: 'Low', date: '2026-09-01', issued_by: 'Admin User', related_to: 'Inventory' }),
    ];
  }
}
